import { createMountsContainerNode, createNodeRuleSet, createPadNodeUnderMountsContainer, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, FootPrintPadHoleType, FootPrintPadShape, TextAlignment, } from "@buildwithflux/models";
import { metersToMm } from "@buildwithflux/shared";
// eslint-disable-next-line boundaries/element-types
import { PcbLayoutRuleCompiler } from "../../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { TextPlaceholder } from "../../SharedDataModels";
import { replaceAutoWithDefault2, replaceAutoWithDefault3 } from "../utils";
import { designatorFontSize, designatorMargin, epadString, pinMarkerMargin, pinMarkerRadius, silkWidth, } from "./commonConstants";
/**
 * Get the side for a pin in DFN/WSON packages (which have pins on two sides only)
 * Side 0 = left side, Side 1 = right side
 */
function getSide(i, gridX) {
    if (i < gridX) {
        return 0;
    }
    else if (i < gridX * 2) {
        return 1;
    }
    return -1;
}
function dfnInferPinLayout(bakedRules, existingPadNodes) {
    const result = { grid: [], otherPads: [] };
    const withoutPinNumberPadNodes = existingPadNodes.filter((pad) => pad.terminalId === "" || pad.terminalId === undefined);
    withoutPinNumberPadNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const remainingPadsWithNumbers = existingPadNodes.filter((pad) => pad.terminalId !== "" && pad.terminalId !== undefined && pad.terminalId !== epadString);
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const total = gridX * 2; // Two sides with gridX pins each
    for (let i = 0; i < total; i++) {
        const foundMatchingI = remainingPadsWithNumbers.findIndex((pad) => pad.terminalId === `${i + 1}`);
        const foundMatching = remainingPadsWithNumbers[foundMatchingI];
        if (foundMatchingI !== -1) {
            remainingPadsWithNumbers.splice(foundMatchingI, 1);
        }
        const matchingPad = foundMatching ?? withoutPinNumberPadNodes.shift();
        const sideId = getSide(i, gridX);
        if (matchingPad && sideId === -1) {
            result.otherPads.push(matchingPad);
        }
        else {
            const sideArr = result.grid[sideId] ?? [];
            sideArr.push(matchingPad ?? null);
            result.grid[sideId] = sideArr;
        }
    }
    // Add thermal pad to otherPads if it exists
    const thermalPad = existingPadNodes.find((pad) => pad.terminalId === epadString);
    if (thermalPad) {
        result.otherPads.push(thermalPad);
    }
    result.otherPads.push(...withoutPinNumberPadNodes);
    result.otherPads.push(...remainingPadsWithNumbers);
    return result;
}
export function dfnComputeInferredRules(bakedRules, config, existingPadNodes) {
    const pinSize = replaceAutoWithDefault2(bakedRules.pinSize, { x: config.pinSizeX, y: config.pinSizeY });
    const pinPitch = replaceAutoWithDefault2(bakedRules.pinPitch, { x: config.pinPitch, y: config.pinPitch });
    const padOuterMargin = replaceAutoWithDefault2(bakedRules.padOuterMargin, {
        x: config.padOuterMargin,
        y: config.padOuterMargin,
    });
    // Filter out thermal pad when counting pins
    const pinsMinusEpad = existingPadNodes.filter((pad) => pad.terminalId !== epadString);
    const pinsCount = pinsMinusEpad.length;
    const padCountX = Math.ceil(pinsCount / 2); // Half the pins on each side
    const gridArrayCount = replaceAutoWithDefault2(bakedRules.gridArrayCount, {
        x: config.gridArrayCountX ?? padCountX,
        y: 0, // DFN only has pins on two sides, so y is always 0
    });
    const packageSize = replaceAutoWithDefault3(bakedRules.packageSize, {
        x: gridArrayCount.x * pinPitch.x,
        y: pinSize.y * 3, // Typical package width
        z: config.packageHeight,
    });
    const newBakedRules = {
        ...bakedRules,
        packageSize,
        pinSize,
        pinPitch,
        gridArrayCount,
        padOuterMargin,
    };
    return { ...newBakedRules, pinLayout: dfnInferPinLayout(newBakedRules, existingPadNodes) };
}
export function dfnGeneratePadRules(footprintNodeUid, bakedRules) {
    const result = {};
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const padOuterMargin = bakedRules.padOuterMargin?.x ?? 0;
    const packageSizeX = bakedRules.packageSize?.x ?? 0;
    const totalX = (gridX - 1) * pinPitchX;
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return result;
    for (let sideId = 0; sideId < pinLayout.grid.length; sideId++) {
        const sideArr = pinLayout.grid[sideId];
        if (!sideArr)
            continue;
        for (let i = 0; i < sideArr.length; i++) {
            const matchingPad = sideArr[i];
            if (!matchingPad)
                continue;
            let posX = 0;
            let posY = 0;
            let rotZ = 0;
            if (sideId === 0) {
                posX = -(packageSizeX / 2 - pinSizeY / 2 + padOuterMargin / 2);
                posY = -(i * pinPitchX - totalX / 2);
                rotZ = 270;
            }
            else if (sideId === 1) {
                posX = packageSizeX / 2 - pinSizeY / 2 + padOuterMargin / 2;
                posY = i * pinPitchX - totalX / 2;
                rotZ = 90;
            }
            const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPad.type, matchingPad.uid);
            const ruleSet = {
                uid: matchingPad.uid,
                selector: selector,
                rules: {},
                generatedFromAncestorNodeUid: footprintNodeUid,
            };
            result[ruleSet.uid] = ruleSet;
            const rules = {
                position: `${metersToMm(posX)}mm ${metersToMm(posY)}mm`,
                size: `${metersToMm(pinSizeX)}mm ${metersToMm(pinSizeY + padOuterMargin)}mm`,
                rotationZ: `${rotZ}deg`,
            };
            ruleSet.rules = createNodeRuleSet(rules);
        }
    }
    // Handle thermal pad if present
    const matchingEPad = pinLayout.otherPads.find((pad) => pad.terminalId === epadString);
    if (matchingEPad && bakedRules.thermalPadSize) {
        const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingEPad.type, matchingEPad.uid);
        const ruleSet = {
            uid: matchingEPad.uid,
            selector: selector,
            rules: {},
            generatedFromAncestorNodeUid: footprintNodeUid,
        };
        result[ruleSet.uid] = ruleSet;
        const rules = {
            position: `0 0`,
            padShape: FootPrintPadShape.rectangle,
            padHoleType: FootPrintPadHoleType.SurfaceMountDevice,
            size: `${metersToMm(bakedRules.thermalPadSize.x)}mm ${metersToMm(bakedRules.thermalPadSize.y)}mm`,
        };
        ruleSet.rules = createNodeRuleSet(rules);
    }
    return result;
}
export function dfnGenerateAdditionalPads(footprintNodeUid, bakedRules) {
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return [];
    const existing = pinLayout.otherPads.find((pad) => pad.terminalId === epadString);
    if (!existing && bakedRules.thermalPadSize && bakedRules.thermalPadSize.x > 0 && bakedRules.thermalPadSize.y > 0) {
        const mountsContainerNode = createMountsContainerNode(footprintNodeUid);
        const padNode = createPadNodeUnderMountsContainer(footprintNodeUid + "_epad", "Thermal Pad", mountsContainerNode.uid, footprintNodeUid);
        padNode.pcbNodeRuleSet = createNodeRuleSet({
            position: `0 0`,
            padShape: FootPrintPadShape.rectangle,
            padHoleType: FootPrintPadHoleType.SurfaceMountDevice,
            size: `${metersToMm(bakedRules.thermalPadSize.x)}mm ${metersToMm(bakedRules.thermalPadSize.y)}mm`,
        }, true);
        return [mountsContainerNode, padNode];
    }
    return [];
}
export function dfnGenerateSilkNodes(footprintNodeUid, bakedRules) {
    if (!bakedRules.packageSize)
        return [];
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const totalX = (gridX - 1) * pinPitchX;
    const padOuterMargin = bakedRules.padOuterMargin?.x ?? 0;
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    const packageWidth = bakedRules.packageSize.x;
    const packageHeight = bakedRules.packageSize.y;
    silkPaths.push(`<path d="
              M ${-packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${packageHeight / 2}
              L ${-packageWidth / 2} ${packageHeight / 2}
              Z" stroke-width="${silkWidth}"
              stroke-linecap="round" fill="none" />`);
    const pin1X = -totalX / 2;
    silkPaths.push(`<circle
              cx="${-packageWidth / 2 - pinMarkerMargin - padOuterMargin}"
              cy="${pin1X}"
              r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(0)}mm ${metersToMm(packageHeight / 2 + designatorMargin + padOuterMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
//# sourceMappingURL=dfn.js.map